import {
  KpiUpdate,
  useAdjustMonthError,
  useAdjustMonthValue,
  useUpdateKpiHierarchy,
  ExpertSystemEquipmentType,
  HistoricalDialogData
} from '@domain/expert-system'
import {PlantViewOption, PlantViewOptions} from '@hconnect/common/consts'
import {ExpertSystemHierarchyNodeType} from '@hconnect/common/types'
import {FC, useMemo} from 'react'

import {
  ExpertSystemDailyTable,
  ExpertSystemUsageTable,
  ResponsiblePersonDialog
} from '../components'
import {useEditResponsibleUsers} from '../hooks'
import {HierarchyTableNode} from '../types'
import {getHierarchyNodes} from '../utils'

type ExpertSystemUsageTableContainerProps = {
  data?: HierarchyTableNode[]
  plantView: PlantViewOption
  getIsExpanded: (id: string) => boolean
  toggleExpandCollapse: (id: string) => void
  equipmentType: ExpertSystemEquipmentType
  isEditMode: boolean
  year: number
  setNodeMonth: (data: HistoricalDialogData) => void
  isMonthlyView: boolean
  date: Date
}

export const ExpertSystemUsageTableContainer: FC<ExpertSystemUsageTableContainerProps> = ({
  data = [],
  plantView,
  getIsExpanded,
  toggleExpandCollapse,
  equipmentType,
  isEditMode,
  year,
  setNodeMonth,
  isMonthlyView,
  date
}) => {
  const plantsData = useMemo(
    () => getHierarchyNodes(data, ExpertSystemHierarchyNodeType.Plant),
    [data]
  )

  const {handleEditClick, users, selectedItem, setSelectedItem} = useEditResponsibleUsers()

  const {mutateAsync: updateKpiHierarchy, isPending: saving} = useUpdateKpiHierarchy()

  const tableData = useMemo(() => {
    return plantView === PlantViewOptions.REGIONAL_STRUCTURE ? data : plantsData
  }, [data, plantView, plantsData])

  const showMillColumns = equipmentType === ExpertSystemEquipmentType.Mill

  const {mutate} = useAdjustMonthValue(year, equipmentType)
  const {mutate: saveError} = useAdjustMonthError(year, equipmentType)

  const handleOnSaveChanges = async (payload: KpiUpdate) => {
    await updateKpiHierarchy({
      ...payload,
      equipmentType: equipmentType,
      plantId: selectedItem?.id
    })
    setSelectedItem(undefined)
  }

  return (
    <>
      {isMonthlyView ? (
        <ExpertSystemDailyTable
          data={tableData}
          getIsExpanded={getIsExpanded}
          toggleExpandCollapse={toggleExpandCollapse}
          showMillColumns={showMillColumns}
          date={date}
          isEditMode={false}
          equipmentType={equipmentType}
          onMonthValueChange={() => undefined}
          setNodeMonth={setNodeMonth}
        />
      ) : (
        <ExpertSystemUsageTable
          isEditMode={isEditMode}
          data={tableData}
          setNodeMonth={setNodeMonth}
          getIsExpanded={getIsExpanded}
          toggleExpandCollapse={toggleExpandCollapse}
          showMillColumns={showMillColumns}
          onMonthValueChange={(hierarchyId, month, value) => {
            if (value !== null && value !== undefined) {
              typeof value === 'number'
                ? mutate({hierarchyId, month, value})
                : saveError({hierarchyId, month, error: value})
            }
          }}
          year={year}
          equipmentType={equipmentType}
          onEditClick={handleEditClick}
        />
      )}
      {selectedItem && (
        <ResponsiblePersonDialog
          onSave={handleOnSaveChanges}
          onClose={() => setSelectedItem(undefined)}
          open
          users={users}
          title={selectedItem.name}
          equipment={equipmentType}
          selectedItem={selectedItem}
          isLoading={saving}
        />
      )}
    </>
  )
}
